import React, { useState, useContext, useEffect } from 'react';
import Styled from 'styled';
import { Link } from 'react-router-dom';
import queryString from 'query-string';
import { __RouterContext } from 'react-router';

import SVGIcon from 'lib/images/svg-icon/svg-icon';
import { usePrevious } from 'lib/react/use-previous';
import { keyEnterPressed } from 'lib/key-code/key-code';

import arrowLeft from 'four-nets/icons/svg/icon_arrow-left.svg';
import arrowRight from 'four-nets/icons/svg/icon_arrow-right.svg';

import defaultStyles from './offset-pager.scss';

interface Props {
    styles: typeof defaultStyles;
    offset?: number;
    pageSize: number;
}

const addTrailingSlash = (value: string) => {
    if (value.endsWith('/')) {
        return value;
    }
    return `${value}/`;
};

const OffsetPager: React.FC<Props> = props => {
    const { styles, offset: nextCursorOffset = 0, pageSize } = props;

    const offset = nextCursorOffset - pageSize;

    const routerContext = useContext(__RouterContext);
    const pathname = addTrailingSlash(routerContext.location.pathname);

    const pageToOffset = (page: number) => {
        if (page > 1) {
            return (page - 1) * pageSize;
        }
    };

    const offsetToPage = (offset: number) => {
        if (offset) {
            return Math.ceil(offset / pageSize) + 1;
        }
    };

    const searchFromOffset = (offsetValue?: number) => {
        return `?${queryString.stringify({
            ...queryString.parse(routerContext.location.search),
            offset: offsetValue,
        })}`;
    };

    const pushOffset = (offsetValue?: number) => {
        routerContext.history.push({
            pathname,
            search: searchFromOffset(offsetValue),
        });
    };

    const nextPage = () => {
        const page = offsetToPage(offset);
        const nextPageOffset = pageToOffset((page || 1) + 1);
        return `${pathname}${searchFromOffset(nextPageOffset)}`;
    };

    const prevPage = () => {
        const page = offsetToPage(offset);
        const prevPageOffset = pageToOffset((page || 1) - 1);
        return `${pathname}${searchFromOffset(prevPageOffset)}`;
    };

    const [value, setValue] = useState(`${offsetToPage(offset) || '1'}`);
    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setValue(event.target.value);
    };

    const onKeyUp = (event: React.KeyboardEvent) => {
        if (keyEnterPressed(event)) {
            const offsetValue = pageToOffset(parseInt(value, 10));
            pushOffset(offsetValue);
        }
    };

    const prevOffset = usePrevious(offset);
    useEffect(() => {
        if (offset !== prevOffset) {
            setValue(`${offsetToPage(offset) || '1'}`);
        }
    }, [nextCursorOffset]);

    return (
        <nav className={styles.nav}>
            <ul className={styles.elements}>
                {offset && offset > 0 ? (
                    <li className={styles.element}>
                        <Link to={prevPage()} className={styles.pagerElement}>
                            <SVGIcon style={{ image: styles.icon }} src={arrowLeft} />
                        </Link>
                    </li>
                ) : null}
                <li className={styles.element} onKeyUp={onKeyUp}>
                    <input className={styles.inputPagerElement} value={value} onChange={onChange} />
                </li>
                <li className={styles.element}>
                    <Link to={nextPage()} className={styles.pagerElement}>
                        <SVGIcon style={{ image: styles.icon }} src={arrowRight} />
                    </Link>
                </li>
            </ul>
        </nav>
    );
};

export default Styled(defaultStyles)(OffsetPager);
